import { Handle, Position } from '@xyflow/react'
import { useState, useEffect } from 'react'
import { isFired } from './useFiredFlash'
import { CustomAssetManager } from '../../lib/CustomAssetManager.js'

/**
 * Sentence control action nodes — PlayFirstSentence and PlayNextSentence.
 * Unreal equivalents: UAvatarOSVoiceToTextComponent::PlayFirstSentence / PlayNextSentence.
 *
 * These are exec nodes (exec-in left, exec-out right) — no data pins.
 * Teal/green color scheme to distinguish from event nodes (orange) and PrintString (blue).
 */

const HEADER_BG     = '#0a3028'
const HEADER_ACTIVE = '#10503a'
const ACCENT        = '#40d090'
const BORDER_SEL    = '#40d090'
const BORDER_IDLE   = '#0d4030'

function ControlNodeShell({ selected, data, label, icon, desc }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_SEL : BORDER_IDLE}`,
      borderRadius: 6,
      minWidth: 200,
      fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? '0 0 12px rgba(40,200,120,0.15)' : 'none',
    }}>
      {/* Header with exec handles */}
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        position: 'relative',
      }}>
        {/* Exec IN */}
        <Handle
          type="target"
          position={Position.Left}
          id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }}
        />
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
          {label}
        </span>
        {/* Exec OUT */}
        <Handle
          type="source"
          position={Position.Right}
          id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }}
        />
      </div>

      <div style={{ padding: '6px 12px', color: '#1a4030', fontSize: 10 }}>
        {desc}
      </div>
    </div>
  )
}

export function PlayFirstSentenceNode({ data, selected }) {
  return <ControlNodeShell selected={selected} data={data} label="Play First Sentence" icon="⏮" desc="Start playing sentence 0 from the queue." />
}

export function PlayNextSentenceNode({ data, selected }) {
  return <ControlNodeShell selected={selected} data={data} label="Play Next Sentence" icon="⏭" desc="Advance to the next sentence in the queue." />
}

export function StopSpeechNode({ data, selected }) {
  return <ControlNodeShell selected={selected} data={data} label="Stop Speech" icon="⏹" desc="Stop audio playback and clear the sentence queue." />
}

// Stream First/Last Frame — publish a queued sentence's first (or last) ARKit
// frame as a static face pose for the face-graph First/Last Frame Pose node. The
// optional `in-index` pin targets a sentenceNum (wire an event's sentenceNum);
// unwired, First defaults to the next sentence to play, Last to the current one.
function StreamFrameNode({ data, selected, label, icon, desc, indexHint }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_SEL : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? '0 0 12px rgba(40,200,120,0.15)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>{label}</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>
      <div style={{ padding: '6px 12px', color: '#1a4030', fontSize: 10 }}>{desc}</div>
      <div style={{ padding: '4px 12px 8px 20px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-index"
          style={{ background: '#80c8e8', width: 10, height: 10, borderRadius: '50%', border: '2px solid #80c8e866' }} />
        <span style={{ color: '#1a4030', fontSize: 9 }}>sentence index (optional — {indexHint})</span>
      </div>
    </div>
  )
}

export function StreamFirstFrameNode({ data, selected }) {
  return <StreamFrameNode selected={selected} data={data} label="Stream First Frame" icon="⏭"
    desc="Publish a sentence's first frame as a static face pose." indexHint="defaults to next sentence" />
}

export function StreamLastFrameNode({ data, selected }) {
  return <StreamFrameNode selected={selected} data={data} label="Stream Last Frame" icon="⏹"
    desc="Publish a sentence's last frame as a static face pose." indexHint="defaults to current sentence" />
}

// Plays a local audio file one-shot (non-streaming). Used for client-side prefab audio.
// Live list of the character's content-browser audio assets. CustomAssetManager
// is the module store the content browser itself reads; subscribing keeps the
// dropdown in step when a clip is uploaded or deleted without a reload.
function useAudioAssets() {
  const [assets, setAssets] = useState(() => CustomAssetManager.listAudioAssets())
  useEffect(() => CustomAssetManager.subscribe(() => setAssets(CustomAssetManager.listAudioAssets())), [])
  return assets
}

// Play Audio: pick a content-browser audio clip from the dropdown. The node is
// ALWAYS a dropdown — deliberately no free-text URL box, which only confused
// authors (a typed path that resolves to nothing looks identical to a working
// one). It stores the chosen asset id; the runtime plays that row's server
// file_url. A wired in-url pin still overrides it for graph-computed URLs, and
// the runtime keeps honouring a legacy data.url on old blueprints — neither is
// authorable here. Manipulable by MCP: the agent lists audio-assets and sets
// data.audioAssetId via update_node.
export function PlayAudioNode({ data, selected }) {
  const fired = isFired(data)
  const stop  = e => e.stopPropagation()
  const assets = useAudioAssets()
  const assetId = data.audioAssetId ?? null

  // A stored id whose asset isn't in the live list (deleted, or another
  // character's) must stay visible + selected, not silently blank — mirrors the
  // LoadEnvironment "missing value stays flagged" rule.
  const missingId = assetId && !assets.some(a => a.id === assetId)

  const fieldStyle = {
    width: '100%', background: '#020a06', border: '1px solid #0d3020',
    color: '#60d090', padding: '4px 6px', borderRadius: 3, fontSize: 10,
    fontFamily: 'var(--m-mono)', outline: 'none', boxSizing: 'border-box',
  }

  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_SEL : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 220, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>🔊</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Play Audio</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>
      <div style={{ padding: '8px 12px' }}>
        {assets.length === 0 && !missingId ? (
          // Loud + inert, with the fix named. Not a dead select, and NOT a
          // fallback to a typed URL — upload a clip first.
          <div style={{ color: '#3a7050', fontSize: 9, lineHeight: 1.5 }}>
            No audio assets — upload a .wav / .mp3 in the content browser.
          </div>
        ) : (
          <>
            <div style={{ color: '#1a4030', fontSize: 9, marginBottom: 3 }}>AUDIO CLIP</div>
            <select
              className="nodrag" aria-label="Audio clip"
              value={assetId ?? ''}
              onChange={e => {
                const id = e.target.value || null
                const row = assets.find(a => a.id === id)
                data.onChange?.('audioAssetId', id)
                data.onChange?.('audioAssetName', row?.name ?? '')
              }}
              onMouseDown={stop} style={{ ...fieldStyle, cursor: 'pointer' }}
            >
              <option value="">— select audio —</option>
              {missingId && (
                <option value={assetId} style={{ color: '#f0b050' }}>
                  {data.audioAssetName || assetId} (missing)
                </option>
              )}
              {assets.map(a => (
                <option key={a.id} value={a.id}>{a.name ?? a.id}</option>
              ))}
            </select>
          </>
        )}
      </div>
      <div style={{ padding: '4px 12px 8px 20px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-url"
          style={{ background: '#c060e8', width: 10, height: 10, borderRadius: '50%', border: '2px solid #c060e866' }} />
        <span style={{ color: '#1a4030', fontSize: 9 }}>URL (wired overrides selection)</span>
      </div>
      {/* out-duration: real audio length in seconds, populated once the
          element has loaded metadata. Wire to a Delay's in-duration pin
          to make the chain wait the audio's actual length. */}
      <div style={{ padding: '4px 12px 8px 12px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative', justifyContent: 'flex-end' }}>
        <span style={{ color: '#1a4030', fontSize: 9 }}>duration (s)</span>
        <Handle type="source" position={Position.Right} id="out-duration"
          style={{ background: '#60a0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60a0d066' }} />
      </div>
    </div>
  )
}

// ── Debug / authoring helper nodes ──────────────────────────────────────────

export function PlayFaceClipNode({ data, selected }) {
  const stop = e => e.stopPropagation()
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_SEL : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 200, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>🎭</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Play Face Clip</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>
      <div style={{ padding: '8px 12px' }}>
        <div style={{ color: '#1a4030', fontSize: 9, marginBottom: 3 }}>CLIP NAME</div>
        <select
          value={data.clipName ?? 'listening_neutral_face'}
          onChange={e => data.onChange?.('clipName', e.target.value)}
          onMouseDown={stop}
          style={{
            width: '100%', background: '#020a06', border: '1px solid #0d3020',
            color: '#60d090', padding: '4px 6px', borderRadius: 3, fontSize: 10,
            fontFamily: 'var(--m-mono)', outline: 'none',
          }}
        >
          <option value="listening_neutral_face">listening_neutral_face</option>
          <option value="thinking_01_face">thinking_01_face</option>
          <option value="responding_face">responding_face</option>
          <option value="prefab_greeting_face">prefab_greeting_face</option>
        </select>
      </div>
    </div>
  )
}

export function ListClipsNode({ data, selected }) {
  return <ControlNodeShell selected={selected} data={data} label="List Clips" icon="📋" desc="Log all registered body + face clips to console." />
}

export function LogStateNode({ data, selected }) {
  return <ControlNodeShell selected={selected} data={data} label="Log State" icon="🔍" desc="Dump current state machine + variables to console." />
}

// Triggers a face gesture clip by name (wired from OnSentenceStarted.animName).
export function PlayGestureNode({ data, selected }) {
  const fired = isFired(data)
  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_SEL : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 200, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>👋</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Play Gesture</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>
      <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-name"
          style={{ background: '#90d890', width: 10, height: 10, borderRadius: '50%', border: '2px solid #50a05066' }} />
        <span style={{ color: '#1a4030', fontSize: 9, letterSpacing: 0.5 }}>GESTURE NAME (wire from OnSentenceStarted.animName)</span>
      </div>
    </div>
  )
}

// Sets ConversationalState = PreviousConversationalState (restores prior state).
const bodyClipNames = () => CustomAssetManager.listAnimationAssets().filter(a => a.kind === 'body').map(a => a.name)

export function PlayBodyClipNode({ data, selected }) {
  const fired = isFired(data)
  const stop  = e => e.stopPropagation()
  const [clips, setClips] = useState(bodyClipNames)

  // Source body clip names live from CustomAssetManager + subscribe, so an
  // upload or an out-of-band sync + Refresh appears without a page reload.
  useEffect(() => {
    const update = () => setClips(bodyClipNames())
    update()
    return CustomAssetManager.subscribe(update)
  }, [])

  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_SEL : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 220, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>🎬</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Play Body Clip</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>
      {/* Clip dropdown (fallback when in-name not wired) */}
      <div style={{ padding: '8px 12px' }}>
        {clips.length > 0 ? (
          <select
            value={data.clipName ?? ''}
            onChange={e => data.onChange?.('clipName', e.target.value)}
            onMouseDown={stop}
            style={{
              width: '100%', background: '#020a06', border: '1px solid #0d3020',
              color: '#60d090', padding: '4px 6px', borderRadius: 3, fontSize: 10,
              fontFamily: 'var(--m-mono)', outline: 'none',
            }}
          >
            {(!clips.includes(data.clipName) && data.clipName) && <option value={data.clipName}>{data.clipName}</option>}
            {clips.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <input value={data.clipName ?? ''} onChange={e => data.onChange?.('clipName', e.target.value)}
            onMouseDown={stop} placeholder="clip name"
            style={{
              width: '100%', background: '#020a06', border: '1px solid #0d3020',
              color: '#60d090', padding: '4px 6px', borderRadius: 3, fontSize: 10,
              fontFamily: 'var(--m-mono)', outline: 'none', boxSizing: 'border-box',
            }} />
        )}
      </div>
      {/* in-name data pin (overrides dropdown when wired) */}
      <div style={{ padding: '4px 12px 4px 20px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-name"
          style={{ background: '#e07040', width: 10, height: 10, borderRadius: '50%', border: '2px solid #e0704044' }} />
        <span style={{ color: '#1a4030', fontSize: 9 }}>CLIP NAME (wired overrides dropdown)</span>
      </div>
      {/* Play speed — wirable pin + fallback input */}
      <div
        className="nodrag nowheel"
        style={{ padding: '4px 12px 8px 12px', position: 'relative', display: 'flex', alignItems: 'center', gap: 8 }}
        onPointerDown={stop}
        onMouseDown={stop}
        onClick={stop}
      >
        <Handle type="target" position={Position.Left} id="in-speed"
          style={{ background: '#60b0d0', width: 10, height: 10, borderRadius: '50%', border: '2px solid #60b0d044' }} />
        <span style={{ color: '#2a6050', fontSize: 10, minWidth: 40 }}>Speed</span>
        <input
          type="number" min={0.1} max={4} step={0.1}
          value={data.playSpeed ?? 1}
          onChange={e => {
            const v = parseFloat(e.target.value)
            if (Number.isFinite(v) && v > 0) data.onChange?.('playSpeed', v)
          }}
          onPointerDown={stop}
          onMouseDown={stop}
          onClick={stop}
          onKeyDown={stop}
          onWheel={stop}
          style={{
            flex: 1,
            background: '#020a06', border: '1px solid #0d3020',
            color: ACCENT, padding: '3px 6px',
            borderRadius: 3, fontSize: 11,
            fontFamily: 'var(--m-mono)', outline: 'none', textAlign: 'right',
          }}
        />
      </div>
    </div>
  )
}

export function RestorePreviousStateNode({ data, selected }) {
  return <ControlNodeShell selected={selected} data={data} label="Restore Previous State" icon="↩" desc="Sets ConversationalState back to whatever it was before the last change." />
}

// ── Play Prefab ───────────────────────────────────────────────────────────────
export function PlayPrefabNode({ data, selected }) {
  const fired = isFired(data)
  const stop  = e => e.stopPropagation()
  const [prefabs, setPrefabs] = useState(() => CustomAssetManager.listPrefabs())

  useEffect(() => {
    setPrefabs([...CustomAssetManager.listPrefabs()])
    return CustomAssetManager.subscribe(() => setPrefabs([...CustomAssetManager.listPrefabs()]))
  }, [])

  const sel = {
    width: '100%', background: '#020a06', border: '1px solid #0d3020',
    color: '#60d090', padding: '4px 6px', borderRadius: 3, fontSize: 10,
    fontFamily: 'var(--m-mono)', outline: 'none',
  }

  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_SEL : BORDER_IDLE}`,
      borderRadius: 6, minWidth: 230, fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : 'none',
    }}>
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
      }}>
        <Handle type="target" position={Position.Left} id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
        <span style={{ fontSize: 13 }}>🎭</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Play Prefab</span>
        <Handle type="source" position={Position.Right} id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }} />
      </div>

      {/* Prefab dropdown */}
      <div style={{ padding: '8px 12px' }}>
        {prefabs.length > 0 ? (
          <select value={data.tag ?? ''} onChange={e => data.onChange?.('tag', e.target.value)}
            onMouseDown={stop} style={sel}>
            <option value="">— select prefab —</option>
            {prefabs.map(p => (
              <option key={p.id} value={p.tag}>{p.display_name || p.tag || '(unnamed)'}</option>
            ))}
          </select>
        ) : (
          <input value={data.tag ?? ''} onChange={e => data.onChange?.('tag', e.target.value)}
            onMouseDown={stop} placeholder="prefab tag"
            style={{ ...sel, boxSizing: 'border-box' }} />
        )}
      </div>

      {/* Non-blocking toggle */}
      <div style={{ padding: '0 12px 6px', display: 'flex', alignItems: 'center', gap: 5 }}>
        <input type="checkbox" checked={!!data.nonBlocking}
          onChange={e => data.onChange?.('nonBlocking', e.target.checked)} onMouseDown={stop} />
        <span style={{ color: '#446654', fontSize: 9 }}>non-blocking (advance immediately)</span>
      </div>

      {/* in-tag data pin — wired value overrides dropdown */}
      <div style={{ padding: '4px 12px 8px 20px', display: 'flex', alignItems: 'center', gap: 5, position: 'relative' }}>
        <Handle type="target" position={Position.Left} id="in-tag"
          style={{ background: '#e07040', width: 10, height: 10, borderRadius: '50%', border: '2px solid #e0704044' }} />
        <span style={{ color: '#1a4030', fontSize: 9 }}>TAG (wired overrides dropdown)</span>
      </div>
    </div>
  )
}

// ── Send Text Input ──────────────────────────────────────────────────────────
export function SendTextInputNode({ data, selected }) {
  const fired = isFired(data)
  const stop = e => e.stopPropagation()
  return (
    <div style={{
      background: selected ? '#081a12' : '#050e0a',
      border: `2px solid ${fired ? '#ffcc44' : selected ? BORDER_SEL : BORDER_IDLE}`,
      borderRadius: 6,
      minWidth: 220,
      fontFamily: 'var(--m-font)',
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? '0 0 12px rgba(40,200,120,0.15)' : 'none',
    }}>
      {/* Header with exec handles */}
      <div style={{
        background: selected ? HEADER_ACTIVE : HEADER_BG,
        padding: '6px 12px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        position: 'relative',
      }}>
        <Handle
          type="target"
          position={Position.Left}
          id="execIn"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }}
        />
        <span style={{ fontSize: 13 }}>📤</span>
        <span style={{ color: ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>
          Send Text Input
        </span>
        <Handle
          type="source"
          position={Position.Right}
          id="execOut"
          style={{ background: '#ddd', width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }}
        />
      </div>

      {/* Text input with data pin */}
      <div style={{ padding: '8px 12px', position: 'relative' }}>
        <div style={{ color: '#1a4030', fontSize: 9, marginBottom: 3, letterSpacing: 0.5 }}>TEXT</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Handle
            type="target"
            position={Position.Left}
            id="in-text"
            style={{ background: '#c060e8', width: 10, height: 10, borderRadius: '50%', border: '2px solid #80309866' }}
          />
          <input
            value={data.text ?? ''}
            onChange={e => data.onChange?.('text', e.target.value)}
            onMouseDown={stop}
            placeholder="Hello!"
            style={{
              width: '100%', boxSizing: 'border-box',
              background: '#020a06', border: '1px solid #0d3020',
              color: '#60d090', padding: '4px 7px',
              borderRadius: 3, fontSize: 10,
              fontFamily: 'var(--m-mono)', outline: 'none',
            }}
          />
        </div>
      </div>
    </div>
  )
}
